// One row in an activity feed. Reused by Dashboard ("Recent activity" panel),
// Activities screen (full feed), and Inbox (filtered to call/note/email types).
// Picks an icon + tint per activity type so the feed scans quickly.

import React from "react";
import { useTeamMembers } from "../hooks/useTeamMembers";
import { findMemberForActivity, activityDisplayName } from "../utils/resolveActivityMember";
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ArrowRightLeft,
  StickyNote,
  UserPlus,
  UserMinus,
  PhoneCall,
  PhoneOutgoing,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  Paperclip,
  AlertTriangle,
  Layers,
  Bell,
  BellOff,
  Moon,
  Mail,
  MailOpen,
  Reply,
  Video,
  FileText,
  Flag,
  ListChecks,
  FolderKanban,
  Sparkles,
  Copy,
  Type as TypeIcon,
  Building2,
  Phone as PhoneIcon,
  Globe,
  DollarSign,
  Coins,
  CalendarClock,
  Tag,
  MessageCircle,
  AlertCircle,
  Activity as ActivityIcon,
} from "lucide-react";

// One source of truth for activity → icon + tone + short label. The label
// renders below the description as the muted "type" caption. Keep entries
// alphabetical inside each group so adding new ones stays readable.
const TYPE_META = {
  // Lead lifecycle
  LEAD_CREATED:    { icon: Plus,            tone: "primary",  label: "Lead created" },
  LEAD_UPDATED:    { icon: Pencil,          tone: "neutral",  label: "Lead updated" },
  LEAD_DELETED:    { icon: Trash2,          tone: "danger",   label: "Lead deleted" },
  LEAD_RESTORED:   { icon: RotateCcw,       tone: "primary",  label: "Lead restored" },
  LEAD_WON:        { icon: CheckCircle2,    tone: "success",  label: "Won" },
  LEAD_LOST:       { icon: XCircle,         tone: "danger",   label: "Lost" },
  LEAD_REOPENED:   { icon: RotateCcw,       tone: "primary",  label: "Reopened" },
  LEAD_COPIED_TO:   { icon: Copy,           tone: "neutral",  label: "Lead copied" },
  LEAD_COPIED_FROM: { icon: Copy,           tone: "neutral",  label: "Lead copied from" },
  OWNER_CHANGED:   { icon: UserPlus,        tone: "primary",  label: "Owner changed" },
  PROJECT_CHANGED: { icon: FolderKanban,    tone: "primary",  label: "Project changed" },
  STATUS_CHANGED:  { icon: ArrowRightLeft,  tone: "neutral",  label: "Status changed" },
  LEAD_STATUS_CHANGED: { icon: ArrowRightLeft, tone: "neutral", label: "Status changed" },
  CO_OWNER_ADDED:   { icon: UserPlus,       tone: "primary",  label: "Co-owner added" },
  CO_OWNER_REMOVED: { icon: UserMinus,      tone: "neutral",  label: "Co-owner removed" },

  // Per-field updates (granular timeline rows)
  TITLE_UPDATED:           { icon: TypeIcon,      tone: "neutral",  label: "Title updated" },
  BRAND_UPDATED:           { icon: Building2,    tone: "neutral",  label: "Brand updated" },
  EMAIL_FIELD_UPDATED:     { icon: Mail,         tone: "neutral",  label: "Email updated" },
  PHONE_UPDATED:           { icon: PhoneIcon,    tone: "neutral",  label: "Phone updated" },
  WEBSITE_UPDATED:         { icon: Globe,        tone: "neutral",  label: "Website updated" },
  VALUE_UPDATED:           { icon: DollarSign,   tone: "primary",  label: "Deal value updated" },
  CURRENCY_UPDATED:        { icon: Coins,        tone: "neutral",  label: "Currency updated" },
  SOURCE_UPDATED:          { icon: Globe,        tone: "neutral",  label: "Source updated" },
  SOURCE_CHANNEL_UPDATED:  { icon: Globe,        tone: "neutral",  label: "Source channel updated" },
  NEXT_ACTIVITY_UPDATED:   { icon: CalendarClock, tone: "primary", label: "Next activity" },
  EXPECTED_CLOSE_UPDATED:  { icon: CalendarClock, tone: "warning", label: "Expected close updated" },
  LABELS_UPDATED:          { icon: Tag,          tone: "warning",  label: "Labels updated" },
  COMMENTS_UPDATED:        { icon: MessageCircle, tone: "neutral", label: "Comments updated" },
  LOST_REASON_UPDATED:     { icon: AlertCircle,  tone: "danger",   label: "Lost reason updated" },
  VENDOR_SEGMENT_UPDATED:  { icon: Layers,       tone: "neutral",  label: "Vendor segment updated" },

  // Pipeline
  PIPELINE_MOVE:   { icon: ArrowRightLeft,  tone: "primary",  label: "Pipeline move" },
  PIPELINE_REMOVE: { icon: Trash2,          tone: "neutral",  label: "Removed from pipeline" },

  // Notes
  NOTE_ADDED:      { icon: StickyNote,      tone: "warning",  label: "Note added" },
  NOTE_DELETED:    { icon: Trash2,          tone: "neutral",  label: "Note deleted" },

  // Contacts
  CONTACT_ADDED:   { icon: UserPlus,        tone: "primary",  label: "Contact added" },
  CONTACT_UPDATED: { icon: Pencil,          tone: "neutral",  label: "Contact updated" },
  CONTACT_DELETED: { icon: Trash2,          tone: "neutral",  label: "Contact removed" },

  // Calls
  CALL_LOGGED:     { icon: PhoneCall,       tone: "primary",  label: "Call logged" },
  CALL_UPDATED:    { icon: Pencil,          tone: "neutral",  label: "Call updated" },
  CALL_INITIATED:  { icon: PhoneOutgoing,   tone: "primary",  label: "Call initiated" },
  CALL_COMPLETED:  { icon: PhoneCall,       tone: "success",  label: "Call completed" },
  CALL_FAILED:     { icon: PhoneOff,        tone: "danger",   label: "Call failed" },
  CALL_RECEIVED:   { icon: PhoneIncoming,   tone: "success",  label: "Call received" },
  CALL_MISSED:     { icon: PhoneMissed,     tone: "warning",  label: "Missed call" },
  CALL_NOTE_ADDED: { icon: StickyNote,      tone: "warning",  label: "Call note" },
  CALL_DELETED:    { icon: Trash2,          tone: "neutral",  label: "Call deleted" },

  // Email
  EMAIL_SENT:      { icon: Mail,            tone: "primary",  label: "Email sent" },
  EMAIL_REPLIED:   { icon: Reply,           tone: "primary",  label: "Email reply sent" },
  EMAIL_RECEIVED:  { icon: MailOpen,        tone: "success",  label: "Email received" },

  // Meetings
  MEETING_CREATED:    { icon: Video,        tone: "primary",  label: "Meeting scheduled" },
  MEETING_CANCELED:   { icon: XCircle,      tone: "danger",   label: "Meeting canceled" },
  MEETING_SYNCED:     { icon: RotateCcw,    tone: "primary",  label: "Meeting synced" },
  MEETING_TRANSCRIPT_ADDED: { icon: FileText, tone: "warning", label: "Transcript added" },
  MEETING_SUMMARY_READY: { icon: Sparkles,  tone: "success",  label: "AI summary ready" },

  // Milestones
  MILESTONE_ADDED:     { icon: Flag,        tone: "primary",  label: "Milestone added" },
  MILESTONE_UPDATED:   { icon: Pencil,      tone: "neutral",  label: "Milestone updated" },
  MILESTONE_COMPLETED: { icon: CheckCircle2, tone: "success", label: "Milestone completed" },
  MILESTONE_REOPENED:  { icon: RotateCcw,   tone: "primary",  label: "Milestone reopened" },
  MILESTONE_DELETED:   { icon: Trash2,      tone: "neutral",  label: "Milestone deleted" },

  // Onboarding
  ONBOARDING_ADDED:     { icon: ListChecks,  tone: "primary",  label: "Onboarding step added" },
  ONBOARDING_UPDATED:   { icon: Pencil,      tone: "neutral",  label: "Onboarding step updated" },
  ONBOARDING_COMPLETED: { icon: CheckCircle2, tone: "success", label: "Onboarding step done" },
  ONBOARDING_REOPENED:  { icon: RotateCcw,   tone: "primary",  label: "Onboarding step reopened" },
  ONBOARDING_DELETED:   { icon: Trash2,      tone: "neutral",  label: "Onboarding step deleted" },
  ONBOARDING_FINISHED:  { icon: CheckCircle2, tone: "success", label: "Onboarding finished" },

  // Follow-ups
  FOLLOWUP_SCHEDULED: { icon: Bell,         tone: "primary",  label: "Follow-up scheduled" },
  FOLLOWUP_COMPLETED: { icon: CheckCircle2, tone: "success",  label: "Follow-up done" },
  FOLLOWUP_CLEARED:   { icon: BellOff,      tone: "neutral",  label: "Follow-up cleared" },
  FOLLOWUP_SNOOZED:   { icon: Moon,         tone: "warning",  label: "Follow-up snoozed" },

  // Email follow-up obligations (cron sweep)
  EMAIL_FOLLOWUP_MISSED:    { icon: AlertTriangle, tone: "danger",  label: "Email follow-up missed" },
  EMAIL_FOLLOWUP_ESCALATED: { icon: AlertTriangle, tone: "danger",  label: "Escalated to manager" },
  EMAIL_FOLLOWUP_RESOLVED:  { icon: CheckCircle2,  tone: "success", label: "Email follow-up resolved" },
  EMAIL_FOLLOWUP_DISMISSED: { icon: BellOff,       tone: "neutral", label: "Email follow-up dismissed" },

  // Documents
  DOCUMENT_UPLOADED: { icon: Paperclip,     tone: "primary",  label: "File uploaded" },
  DOCUMENT_DELETED:  { icon: Trash2,        tone: "neutral",  label: "File deleted" },
};

const TONE_BG = {
  primary: "var(--crm-primary-soft)",
  success: "var(--crm-success-soft, rgba(16,185,129,0.12))",
  danger:  "var(--crm-danger-soft, rgba(239,68,68,0.12))",
  warning: "var(--crm-warning-soft, rgba(245,158,11,0.12))",
  neutral: "var(--color-bg-muted)",
};
const TONE_FG = {
  primary: "var(--crm-primary)",
  success: "var(--crm-success, rgb(16,185,129))",
  danger:  "var(--crm-danger, rgb(239,68,68))",
  warning: "var(--crm-warning, rgb(245,158,11))",
  neutral: "var(--color-text-secondary)",
};

function formatTime(d) {
  if (!d) return "";
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(d).toLocaleDateString();
}

export default function ActivityRow({ activity, onLeadClick, compact = false }) {
  const meta = TYPE_META[activity.type] || { icon: ActivityIcon, tone: "neutral", label: activity.type };
  const Icon = meta.icon;
  const leadTitle = activity.lead?.title;
  const clickable = !!(leadTitle && onLeadClick && activity.leadId);
  const { byId, members } = useTeamMembers();
  const member = findMemberForActivity(activity, { byId, members });
  const displayName = activityDisplayName(activity, member);
  const titleEmail = activity.user?.email || activity.userEmail || undefined;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: compact ? "10px 0" : "14px 16px",
        borderBottom: compact ? "none" : "1px solid var(--color-border-subtle)",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 8,
          background: TONE_BG[meta.tone],
          color: TONE_FG[meta.tone],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, lineHeight: "20px", color: "var(--color-text-primary)" }}>
          <span style={{ fontWeight: 600 }} title={titleEmail}>{displayName}</span>
          {" "}
          <span style={{ color: "var(--color-text-secondary)" }}>{activity.description}</span>
          {leadTitle && (
            <>
              {" "}
              <span style={{ color: "var(--color-text-secondary)" }}>on </span>
              <span
                onClick={clickable ? () => onLeadClick(activity.leadId) : undefined}
                style={{
                  fontWeight: 500,
                  color: clickable ? "var(--crm-primary)" : "var(--color-text-primary)",
                  cursor: clickable ? "pointer" : "default",
                }}
              >
                {leadTitle}
              </span>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
          <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.label}</span>
          <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>·</span>
          <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{formatTime(activity.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}
